import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import rolesService from '../../service/api/RolesService';
import permissionsService from '../../service/api/PermissionsService';
import { useTranslation } from 'react-i18next';
import { Chip } from 'primereact/chip';
import RoleDialog from './RoleDialog';
import { RoleDetails } from '../../types/Role';
import { Permission } from '../../types/Permission';

export const RoleManagement = () => {
  const [roles, setRoles] = useState<RoleDetails[]>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<RoleDetails>(null);
  const [dialogVisible, setDialogVisible] = useState(false);

  const [permissions, setPermissions] = useState<Permission[]>();
  const { t } = useTranslation();

  const loadData = () => {
    setLoading(true);
    Promise.all([rolesService.getDetails(), permissionsService.getAll()]).then((response) => {
      setPermissions(addPermissionsLabels(response[1]));
      setRoles(
        response[0].map((role) => {
          const permissions = addPermissionsLabels(role.permissions);
          return {
            ...role,
            permissions: permissions,
            permissionsSearch: permissions.map((p) => p.name).join(' '),
          };
        })
      );
      setLoading(false);
    });
  };

  const addPermissionsLabels = (permissions: Permission[]) => {
    return permissions.map((permission) => {
      return { id: permission.id, name: t(`permissions.${permission.id}`) };
    });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableHeader = (
    <div className="table-header">
      <h3 className="m-0">{t('roles.header')}</h3>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
      </span>
    </div>
  );

  const editRole = (_role: RoleDetails) => {
    setSelectedRowToEdit(_role);
    setDialogVisible(true);
  };

  const closeAddEditModal = () => {
    setDialogVisible(false);
    loadData();
  };

  const bodyTemplate = (data: RoleDetails, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const permissionsTemplate = (data: any, props: ColumnBodyOptions) => {
    return (
      <>
        {data[props.field].map((item: any) => {
          return item.name ? <Chip key={item.id} label={item.name} className="mr-2 mb-2" /> : null;
        })}
      </>
    );
  };

  const actionTemplate = (data: RoleDetails) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          editRole(data);
        }}
      ></Button>
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={roles}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            loading={loading}
            header={tableHeader}
          >
            <Column field="name" header={t('roles.name')} sortable body={bodyTemplate}></Column>
            <Column
              field="permissions"
              filterField="permissionsSearch"
              headerStyle={{ width: '70%' }}
              header={t('roles.permissions')}
              body={permissionsTemplate}
            ></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <RoleDialog
            dialogVisible={dialogVisible}
            closeAddEditModal={closeAddEditModal}
            role={selectedRowToEdit}
            allPermissions={permissions}
          />
        </div>
      </div>
    </div>
  );
};
